<template>
  <button ref="trigger" type="button" class="city-trigger" :aria-label="`切换城市，当前地区：${regionName}`" title="点击切换城市" aria-haspopup="dialog" @click="open">
    <span class="city-name">{{ regionName }}</span> <i class="bi bi-chevron-down" aria-hidden="true"></i>
  </button>
  <Teleport to="body">
    <div ref="panel" class="modal fade city-modal" tabindex="-1" aria-labelledby="city-selector-title" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h2 id="city-selector-title" class="modal-title fs-5">选择城市</h2>
            <button type="button" class="btn-close" aria-label="关闭城市选择" @click="close"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted">当前地区：{{ regionName }}</p>
            <button type="button" class="national-option" :aria-pressed="!route.params.region_id"
              @click="selectCity({ code: '', name: '中国' })">中国（全国）</button>
            <div class="city-breadcrumb">
              <button type="button" class="btn btn-link p-0" @click="loadRegions()">全部省份</button>
              <span v-if="province"> / {{ province.name }}</span>
            </div>
            <p v-if="loading" role="status" class="text-muted mt-3">正在加载城市…</p>
            <div v-else-if="error" role="alert" class="mt-3">
              <p class="text-danger">城市列表加载失败，请重试</p>
              <button type="button" class="btn btn-outline-primary" @click="loadRegions(province)">重试</button>
            </div>
            <template v-else>
              <div class="city-grid">
                <button v-for="item in regions" :key="item.code" type="button" class="city-option"
                  :class="{ selected: String(item.code) === String(route.params.region_id) }"
                  :title="item.name"
                  :aria-pressed="String(item.code) === String(route.params.region_id)"
                  @click="choose(item)">{{ item.name }}</button>
              </div>
              <p v-if="!regions.length" class="text-muted mt-3">暂无城市信息</p>
              <button v-if="province" type="button" class="btn btn-link px-0 mt-3" @click="selectCity(province)">
                浏览{{ province.name }}全省
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { Modal } from 'bootstrap';
import { cityDestination } from '@/composables/RegionPreference.mjs';

const route = useRoute();
const router = useRouter();
const regionName = ref('中国');
const trigger = ref();
const panel = ref();
const province = ref(null);
const regions = ref([]);
const loading = ref(false);
const error = ref(false);
let modal;
let listVersion = 0;
let nameVersion = 0;
const controller = new AbortController();

watch(() => [route.params.region_id, route.meta.title], async ([code]) => {
  const version = ++nameVersion;
  if (!code) {
    regionName.value = '中国';
    document.title = `时空记忆 - 中国${route.meta.title || ''}`;
    return;
  }
  regionName.value = `地区 ${code}`;
  try {
    const { data } = await axios.get(`/api/region/${code}/`, { signal: controller.signal });
    if (version !== nameVersion) return;
    regionName.value = data.name;
    document.title = `时空记忆 - ${data.name}${route.meta.title || ''}`;
  } catch { /* Keep the picker available even when region lookup fails. */ }
}, { immediate: true });

async function loadRegions(parent = null) {
  const version = ++listVersion;
  province.value = parent;
  loading.value = true;
  error.value = false;
  regions.value = [];
  try {
    const { data } = await axios.get('/api/region/', {
      params: parent ? { code: parent.code } : {}, signal: controller.signal
    });
    if (version === listVersion) regions.value = data;
  } catch {
    if (version === listVersion) error.value = true;
  } finally {
    if (version === listVersion) loading.value = false;
  }
}
function open() {
  modal.show(trigger.value);
  loadRegions();
}
function close() { modal.hide(); }
function restoreFocus() { trigger.value?.focus(); }
function choose(item) {
  if (province.value || ['11', '12', '31', '50', '81', '82'].includes(String(item.code))) {
    selectCity(item);
  } else loadRegions(item);
}
async function selectCity(item) {
  await router.push(cityDestination(route, String(item.code)));
  close();
}
onMounted(() => {
  modal = new Modal(panel.value);
  panel.value.addEventListener('hidden.bs.modal', restoreFocus);
});
onUnmounted(() => {
  controller.abort();
  listVersion++;
  nameVersion++;
  panel.value?.removeEventListener('hidden.bs.modal', restoreFocus);
  modal?.dispose();
});
</script>

<style scoped>
.city-trigger { display: inline-flex; align-items: center; gap: 4px; min-width: 0; max-width: 150px; border: 0; border-radius: 0; background: transparent; color: white; padding: 6px 0; font-size: 18px; min-height: 36px; cursor: pointer; }
.city-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-decoration: underline dotted; text-underline-offset: 4px; text-decoration-thickness: 1px; }
.city-trigger .bi { font-size: 11px; flex-shrink: 0; }
.city-trigger:hover .city-name { text-decoration-style: solid; }
.city-trigger:focus-visible { outline: 2px solid white; outline-offset: 2px; border-radius: 2px; }
.national-option { display: block; background: transparent; border: 0; color: #245c8d; padding: 8px 0 16px; text-decoration: underline; text-underline-offset: 3px; }
.city-modal .modal-dialog { max-width: 560px; margin: 10vh auto; padding: 0 12px; }
.city-modal .modal-body { max-height: 65vh; overflow-y: auto; padding-bottom: max(24px, env(safe-area-inset-bottom)); }
.city-breadcrumb { padding-bottom: 14px; border-bottom: 1px solid #eee; }
.city-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 16px; }
.city-option { border: 1px solid #e1e6ec; border-radius: 6px; background: #f7f9fc; color: #26384b; padding: 10px 6px; min-height: 44px; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.city-option:hover, .city-option.selected { border-color: #477eaf; color: #245c8d; background: #eaf3fc; }
@media (max-width: 575px) {
  .city-modal .modal-dialog { margin: 0; padding: 0; max-width: none; min-height: 100%; display: flex; align-items: flex-end; }
  .city-modal .modal-content { border-radius: 16px 16px 0 0; border: 0; }
}
@media (max-width: 360px) { .city-trigger { font-size: 16px; max-width: 108px; } }
</style>
